import mixpanel from "mixpanel-browser";

import type { AnalyticsAdapter } from "../types";

// Type assertion to ensure proper typing
const mixpanelTyped = mixpanel as {
  init: (token: string, config?: Record<string, unknown>) => void;
  identify: (id: string) => void;
  track: (event: string, props?: Record<string, unknown>) => void;
  reset: () => void;
  register: (props: Record<string, unknown>) => void;
  people: {
    set: (props: Record<string, string | number | boolean>) => void;
  };
};

const isBrowser = typeof window !== "undefined";
let initialized = false;

export class MixpanelAdapter implements AnalyticsAdapter {
  private lastIdentifiedUserId: string | null = null;
  private lastTraitsKey: string | null = null;
  init(): void {
    if (!isBrowser || initialized) return;
    const token = import.meta.env.VITE_MIXPANEL_TOKEN;
    if (!token) return;
    try {
      mixpanelTyped.init(token, {
        track_pageview: false,
        debug: import.meta.env.VITE_DEPLOYMENT_ENVIRONMENT !== "production",
      });

      // Register super properties
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const locale = navigator.language;
      mixpanelTyped.register({
        eventVersion: "v2",
        timezone,
        locale,
      });

      initialized = true;
    } catch {
      console.error("Failed to initialize Mixpanel");
    }
  }

  page(_: { name?: string; properties?: Record<string, unknown> }): void {
    return;
  }

  track(input: { event: string; properties?: Record<string, unknown> }): void {
    if (!isBrowser) return;
    if (!initialized) this.init();

    // Filter out token property
    const { token: _token, ...filteredProperties } = input.properties ?? {};

    mixpanelTyped.track(input.event, filteredProperties);
  }

  identify(input: { userId?: string; traits?: Record<string, unknown> }): void {
    if (!isBrowser) return;
    if (!initialized) this.init();

    // Ensure identify is called before people.set
    if (input.userId) {
      const userId = input.userId;
      const traitsKey = JSON.stringify(input.traits ?? {});

      const userChanged = this.lastIdentifiedUserId !== userId;
      const traitsChanged = this.lastTraitsKey !== traitsKey;

      if (userChanged) {
        mixpanelTyped.identify(userId);
        this.lastIdentifiedUserId = userId;
      }

      // Only set people properties when first identified or when traits changed
      if (input.traits && (userChanged || traitsChanged)) {
        const typedTraits = input.traits as Record<string, string | number | boolean>;
        mixpanelTyped.people.set(typedTraits);
        this.lastTraitsKey = traitsKey;
      }
    }
  }

  reset(): void {
    if (!isBrowser) return;
    if (!initialized) this.init();
    mixpanelTyped.reset();
    this.lastIdentifiedUserId = null;
    this.lastTraitsKey = null;
  }
}
